import { createFileRoute } from '@tanstack/react-router'
import {
  Badge,
  Button,
  Card,
  Content as CardContent,
  Text as CardDescription,
  Header as CardHeader,
  Heading as CardTitle,
  CustomDialog,
  DialogContainer,
  InlineAlert,
  LinkButton,
} from '@react-spectrum/s2'
/**
 * Subscription Management Page
 *
 * This page allows users to view and manage their subscription:
 * - View current plan, status, and billing details
 * - Update payment method
 * - Cancel subscription
 * - View renewal history
 */
import { useState } from 'react'
import {
  AlertTriangle,
  ArrowLeft,
  ArrowUpRight,
  Calendar,
  Check,
  CreditCard,
  Loader2,
  RefreshCw,
  X,
} from 'lucide-react'
import {
  cancelSubscriptionAtPeriodEnd,
  getSubscriptionDetails,
  reactivateSubscription,
} from '@rpc/billing.rpc'
import { trackAnalyticsEvent } from '@client/analytics'
import { APP_ROUTES } from '@shared/app-routes'
import {
  SUBSCRIPTION_CANCELLATION_REASON_LABELS,
  SUBSCRIPTION_CANCELLATION_REASONS,
  type SubscriptionCancellationReason,
} from '@shared/billing'
import { getPublicPlanCard } from '@rpc/plans.rpc'
import {
  flexGrowButtonStyles,
  fullWidthFlexButtonStyles,
  fullWidthStyles,
} from '@/styles/s2-macros'

const subscriptionRoute = createFileRoute('/dashboard/subscription')({
  loader: async () => {
    const details = await getSubscriptionDetails()

    if (!details.hasSubscription || !details.planId) {
      return details
    }

    const plan = await getPublicPlanCard({
      data: { planId: details.planId },
    })
    return { ...details, plan }
  },
  component: SubscriptionManagementPage,
})

function SubscriptionManagementPage() {
  const subscriptionData = subscriptionRoute.useLoaderData()
  const [isCancelling, setIsCancelling] = useState(false)
  const [isReactivating, setIsReactivating] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [cancellationReason, setCancellationReason] = useState<
    SubscriptionCancellationReason | ''
  >('')
  const [cancellationNote, setCancellationNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  if (!subscriptionData.hasSubscription) {
    return (
      <div className="w-full px-4 py-8 md:px-8 md:py-10">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
          <Card styles={fullWidthStyles}>
            <CardHeader>
              <CardTitle>No active subscription</CardTitle>
              <CardDescription>
                You don't have an active subscription yet. Upgrade from your
                dashboard.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <LinkButton
                href={APP_ROUTES.dashboard.home}
                onPress={() =>
                  trackAnalyticsEvent('subscription_view_plans_clicked', {
                    has_subscription: false,
                  })
                }
              >
                View plans
              </LinkButton>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (!('plan' in subscriptionData)) {
    return (
      <div className="w-full px-4 py-8 md:px-8 md:py-10">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
          <Card styles={fullWidthStyles}>
            <CardHeader>
              <CardTitle>Subscription unavailable</CardTitle>
              <CardDescription>
                We couldn't load your subscription plan details right now.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <LinkButton
                href={APP_ROUTES.dashboard.home}
                variant="secondary"
                fillStyle="outline"
              >
                Back to dashboard
              </LinkButton>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  const {
    plan,
    status,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    nextBillingAmount,
    renewalFailureCount,
  } = subscriptionData
  const renewalFailureCountValue = renewalFailureCount ?? 0
  const statusLabel = getStatusLabel(status)
  const statusVariant = getStatusVariant(status)

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A'
    return new Date(dateStr).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatPrice = (amount: number | null) => {
    if (!amount) return 'N/A'
    return `£${(amount / 100).toFixed(2)}`
  }

  function getStatusVariant(subscriptionStatus: string) {
    switch (subscriptionStatus) {
      case 'active':
      case 'trialing':
        return 'positive'
      case 'past_due':
        return 'negative'
      case 'canceled':
        return 'gray'
      case 'incomplete':
        return 'notice'
      default:
        return 'gray'
    }
  }

  const formatPlanPrice = (price: string) =>
    price.endsWith('/mo') ? `${price.slice(0, -3)}/month` : price
  const planFeatures = Array.isArray(plan.features) ? plan.features : []

  const handleCancel = async () => {
    const confirmedReason = cancellationReason as SubscriptionCancellationReason
    setIsCancelling(true)
    setError(null)
    setSuccess(null)
    trackAnalyticsEvent('subscription_cancel_submitted', {
      status,
      cancel_at_period_end: cancelAtPeriodEnd,
      reason: confirmedReason,
    })

    try {
      await cancelSubscriptionAtPeriodEnd({
        data: {
          reason: confirmedReason,
          note: cancellationNote.trim() || null,
        },
      })
      setSuccess(
        'Subscription will be cancelled at the end of the current billing period.',
      )
      setShowCancelDialog(false)
      trackAnalyticsEvent('subscription_cancel_succeeded', {
        status,
        reason: confirmedReason,
      })
      // Refresh the page to show updated status
      setTimeout(() => window.location.reload(), 1500)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to cancel subscription',
      )
      trackAnalyticsEvent('subscription_cancel_failed', {
        status,
        reason: confirmedReason,
      })
    } finally {
      setIsCancelling(false)
    }
  }

  const handleReactivate = async () => {
    setIsReactivating(true)
    setError(null)
    trackAnalyticsEvent('subscription_reactivate_submitted', {
      status,
    })

    try {
      await reactivateSubscription()
      setSuccess('Subscription has been reactivated and will continue.')
      trackAnalyticsEvent('subscription_reactivate_succeeded', {
        status,
      })
      // Refresh the page
      setTimeout(() => window.location.reload(), 1500)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to reactivate subscription',
      )
      trackAnalyticsEvent('subscription_reactivate_failed', {
        status,
      })
    } finally {
      setIsReactivating(false)
    }
  }

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
        <section className="rounded-[2rem] border border-border/70 bg-gradient-to-br from-white via-white to-sky-50/70 p-6 shadow-[0_24px_80px_-40px_rgba(15,23,42,0.3)] dark:from-neutral-950 dark:via-neutral-950 dark:to-sky-950/20 md:p-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                Billing control
              </p>
              <div className="space-y-2">
                <h1 className="text-3xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100">
                  Subscription management
                </h1>
                <p className="max-w-2xl text-sm leading-6 text-neutral-600 dark:text-neutral-300">
                  Review the current plan, check renewal timing, and manage
                  account access without leaving the workspace.
                </p>
              </div>
            </div>
            <LinkButton
              href={APP_ROUTES.dashboard.home}
              variant="secondary"
              fillStyle="outline"
              onPress={() =>
                trackAnalyticsEvent('navigation_clicked', {
                  destination: APP_ROUTES.dashboard.home,
                  label: 'back_to_dashboard',
                  location: 'subscription',
                })
              }
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to dashboard
            </LinkButton>
          </div>
        </section>

        {error && (
          <InlineAlert variant="negative" fillStyle="subtleFill">
            <AlertTriangle className="h-4 w-4" />
            <div className="text-sm [&_p]:leading-relaxed">{error}</div>
          </InlineAlert>
        )}

        {success && (
          <InlineAlert variant="positive" fillStyle="border">
            <Check className="h-4 w-4 text-green-600" />
            <div className="text-sm [&_p]:leading-relaxed text-green-700 dark:text-green-500">
              {success}
            </div>
          </InlineAlert>
        )}

        <section className="grid gap-6 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,0.75fr)]">
          <Card styles={fullWidthStyles}>
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <CardTitle>Current plan</CardTitle>
                  <CardDescription>
                    Your active subscription details
                  </CardDescription>
                </div>
                <Badge variant={statusVariant} fillStyle="bold">
                  {statusLabel}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-5">
                <div className="rounded-[1.6rem] border border-border/80 bg-gradient-to-br from-sky-50 via-white to-cyan-50 p-5 dark:from-sky-950/20 dark:via-neutral-950 dark:to-cyan-950/20">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                        Active access
                      </p>
                      <p className="text-2xl font-semibold tracking-tight text-foreground">
                        {plan.name}
                      </p>
                      <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
                        {plan.description}
                      </p>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="text-2xl font-bold text-foreground">
                        {formatPlanPrice(plan.price)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Recurring plan price
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-border/80 bg-background px-4 py-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 rounded-xl bg-primary/10 p-2 text-primary">
                        <Calendar className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                          Next billing date
                        </p>
                        <p className="mt-2 text-base font-semibold text-foreground">
                          {cancelAtPeriodEnd
                            ? `Expires ${formatDate(currentPeriodEnd)}`
                            : formatDate(currentPeriodEnd)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border/80 bg-background px-4 py-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 rounded-xl bg-primary/10 p-2 text-primary">
                        <CreditCard className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                          Next billing amount
                        </p>
                        <p className="mt-2 text-base font-semibold text-foreground">
                          {formatPrice(nextBillingAmount)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {cancelAtPeriodEnd && (
                  <InlineAlert variant="notice" fillStyle="subtleFill">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <div className="text-sm [&_p]:leading-relaxed text-amber-800 dark:text-amber-500">
                      Your subscription is set to cancel on{' '}
                      {formatDate(currentPeriodEnd)}. You'll still have access
                      until then.
                    </div>
                  </InlineAlert>
                )}

                {renewalFailureCountValue > 0 && (
                  <InlineAlert variant="negative" fillStyle="subtleFill">
                    <AlertTriangle className="h-4 w-4" />
                    <div className="text-sm [&_p]:leading-relaxed">
                      {renewalFailureCountValue} failed payment attempt
                      {renewalFailureCountValue > 1 ? 's' : ''}. Please contact
                      support to resolve billing.
                    </div>
                  </InlineAlert>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card styles={fullWidthStyles}>
              <CardHeader>
                <CardTitle>Manage subscription</CardTitle>
                <CardDescription>
                  {cancelAtPeriodEnd
                    ? 'Your subscription will expire at the end of the current period.'
                    : 'Manage your subscription settings in-app.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex flex-col gap-3">
                    <LinkButton
                      href={APP_ROUTES.pricing}
                      variant="secondary"
                      fillStyle="outline"
                      styles={fullWidthFlexButtonStyles}
                      onPress={() =>
                        trackAnalyticsEvent(
                          'subscription_change_plan_clicked',
                          {
                            status,
                            cancel_at_period_end: cancelAtPeriodEnd,
                          },
                        )
                      }
                    >
                      <ArrowUpRight className="mr-2 h-4 w-4" />
                      Change plan
                    </LinkButton>

                    {cancelAtPeriodEnd ? (
                      <Button
                        variant="primary"
                        onPress={handleReactivate}
                        isDisabled={isReactivating}
                        styles={flexGrowButtonStyles}
                      >
                        {isReactivating ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="mr-2 h-4 w-4" />
                        )}
                        Reactivate subscription
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        fillStyle="outline"
                        onPress={() => {
                          trackAnalyticsEvent(
                            'subscription_cancel_dialog_opened',
                            {
                              status,
                            },
                          )
                          setCancellationReason('')
                          setCancellationNote('')
                          setShowCancelDialog(true)
                        }}
                        styles={flexGrowButtonStyles}
                      >
                        <X className="mr-2 h-4 w-4" />
                        Cancel subscription
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {renewalFailureCountValue > 0 && (
              <Card styles={fullWidthStyles}>
                <CardHeader>
                  <CardTitle>Need billing help?</CardTitle>
                  <CardDescription>
                    Contact support to resolve failed renewals or update payment
                    details.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <LinkButton
                    href="mailto:support@buildingregs.app"
                    onPress={() =>
                      trackAnalyticsEvent('contact_support_clicked', {
                        location: 'subscription',
                        reason: 'renewal_failure',
                      })
                    }
                  >
                    Contact support
                  </LinkButton>
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        <Card styles={fullWidthStyles}>
          <CardHeader>
            <CardTitle>Plan features</CardTitle>
            <CardDescription>
              Included with your {plan.name} plan
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="grid gap-3 sm:grid-cols-2">
              {planFeatures.map((feature: string, i: number) => (
                <li
                  key={i}
                  className="flex items-start gap-3 rounded-2xl border border-border/80 bg-background px-4 py-4"
                >
                  <Check className="mt-0.5 h-4 w-4 text-green-500" />
                  <span className="text-sm leading-6 text-foreground">
                    {feature}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Cancel Confirmation Dialog */}
        {showCancelDialog ? (
          <DialogContainer onDismiss={() => setShowCancelDialog(false)}>
            <CustomDialog isDismissible aria-label="Cancel subscription">
              <div className="space-y-4">
                <h2 className="text-lg font-semibold">Cancel subscription?</h2>
                <p className="text-sm text-neutral-600 dark:text-neutral-300">
                  Are you sure you want to cancel your subscription? You'll
                  continue to have access until the end of your current billing
                  period ({formatDate(currentPeriodEnd)}).
                </p>
                <fieldset className="space-y-3">
                  <legend className="text-sm font-semibold text-foreground">
                    What is the main reason you are cancelling?
                  </legend>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {SUBSCRIPTION_CANCELLATION_REASONS.map((reason) => (
                      <label
                        key={reason}
                        className="flex cursor-pointer items-start gap-2 rounded-xl border border-border px-3 py-2 text-sm"
                      >
                        <input
                          type="radio"
                          name="cancellation-reason"
                          value={reason}
                          checked={cancellationReason === reason}
                          onChange={() => setCancellationReason(reason)}
                          required
                          className="mt-0.5"
                        />
                        <span>
                          {SUBSCRIPTION_CANCELLATION_REASON_LABELS[reason]}
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <label className="block space-y-2 text-sm font-medium text-foreground">
                  <span>Additional details (optional)</span>
                  <textarea
                    value={cancellationNote}
                    onChange={(event) =>
                      setCancellationNote(event.currentTarget.value)
                    }
                    maxLength={500}
                    rows={3}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 font-normal"
                  />
                </label>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    fillStyle="outline"
                    onPress={() => {
                      trackAnalyticsEvent(
                        'subscription_cancel_dialog_dismissed',
                        {
                          status,
                        },
                      )
                      setShowCancelDialog(false)
                    }}
                  >
                    Keep Subscription
                  </Button>
                  <Button
                    variant="negative"
                    onPress={handleCancel}
                    isDisabled={isCancelling || !cancellationReason}
                  >
                    {isCancelling ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Cancelling...
                      </>
                    ) : (
                      'Yes, Cancel subscription'
                    )}
                  </Button>
                </div>
              </div>
            </CustomDialog>
          </DialogContainer>
        ) : null}
      </div>
    </div>
  )
}

function getStatusLabel(status: string) {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'Active'
    case 'past_due':
      return 'Payment issue'
    case 'incomplete':
      return 'Payment pending'
    case 'canceled':
      return 'Cancelled'
    default:
      return 'Unknown'
  }
}

export { subscriptionRoute as Route }
